// Image sampling helpers — bilinear brightness lookup and rectangular
// average reductions (brightness + RGB). Ported from the HTML prototype:
// sampleImageBrightness (lines 817–829), averageBrightness (1006–1021),
// averageColor (1023–1044).

import type { RgbTriplet, SampledImage } from '../types';

export function sampleImageBrightness(img: SampledImage, u: number, v: number): number {
  if (img.brightness.length === 0) return 0;
  const x = u * (img.sampleW - 1);
  const y = v * (img.sampleH - 1);
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(img.sampleW - 1, x0 + 1);
  const y1 = Math.min(img.sampleH - 1, y0 + 1);
  const fx = x - x0;
  const fy = y - y0;
  const a = img.brightness[y0 * img.sampleW + x0] ?? 0;
  const b = img.brightness[y0 * img.sampleW + x1] ?? 0;
  const c = img.brightness[y1 * img.sampleW + x0] ?? 0;
  const d = img.brightness[y1 * img.sampleW + x1] ?? 0;
  return (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy;
}

export function sampleImageAlpha(img: SampledImage, u: number, v: number): number {
  const alpha = img.alpha;
  if (alpha === undefined || alpha.length === 0) return 1;
  const x = Math.max(0, Math.min(img.sampleW - 1, Math.round(u * (img.sampleW - 1))));
  const y = Math.max(0, Math.min(img.sampleH - 1, Math.round(v * (img.sampleH - 1))));
  return alpha[y * img.sampleW + x] ?? 0;
}

export function averageAlpha(
  img: SampledImage,
  u0: number,
  u1: number,
  v0: number,
  v1: number,
): number {
  if (img.alpha === undefined) return 1;
  const { sampleW, sampleH, alpha } = img;
  const x0 = Math.max(0, Math.min(sampleW - 1, Math.floor(u0 * sampleW)));
  const x1 = Math.max(0, Math.min(sampleW - 1, Math.ceil(u1 * sampleW) - 1));
  const y0 = Math.max(0, Math.min(sampleH - 1, Math.floor(v0 * sampleH)));
  const y1 = Math.max(0, Math.min(sampleH - 1, Math.ceil(v1 * sampleH) - 1));
  let sum = 0;
  let count = 0;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      sum += alpha[y * sampleW + x] ?? 0;
      count++;
    }
  }
  return count > 0 ? sum / count : 0;
}

export function averageBrightness(
  img: SampledImage,
  u0: number,
  u1: number,
  v0: number,
  v1: number,
): number {
  if (img.brightness.length === 0) return 0;
  const { sampleW, sampleH, brightness } = img;
  const x0 = Math.max(0, Math.min(sampleW - 1, Math.floor(u0 * sampleW)));
  const x1 = Math.max(0, Math.min(sampleW - 1, Math.ceil(u1 * sampleW) - 1));
  const y0 = Math.max(0, Math.min(sampleH - 1, Math.floor(v0 * sampleH)));
  const y1 = Math.max(0, Math.min(sampleH - 1, Math.ceil(v1 * sampleH) - 1));
  let sum = 0;
  let count = 0;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      sum += brightness[y * sampleW + x] ?? 0;
      count++;
    }
  }
  return count > 0 ? sum / count : 0;
}

export function averageColor(
  img: SampledImage,
  u0: number,
  u1: number,
  v0: number,
  v1: number,
): RgbTriplet {
  if (img.colorData.length === 0) return [0, 0, 0];
  const { sampleW, sampleH, colorData } = img;
  const x0 = Math.max(0, Math.min(sampleW - 1, Math.floor(u0 * sampleW)));
  const x1 = Math.max(0, Math.min(sampleW - 1, Math.ceil(u1 * sampleW) - 1));
  const y0 = Math.max(0, Math.min(sampleH - 1, Math.floor(v0 * sampleH)));
  const y1 = Math.max(0, Math.min(sampleH - 1, Math.ceil(v1 * sampleH) - 1));
  let rSum = 0;
  let gSum = 0;
  let bSum = 0;
  let count = 0;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const idx = (y * sampleW + x) * 3;
      rSum += colorData[idx] ?? 0;
      gSum += colorData[idx + 1] ?? 0;
      bSum += colorData[idx + 2] ?? 0;
      count++;
    }
  }
  if (count === 0) return [0, 0, 0];
  return [rSum / count, gSum / count, bSum / count];
}
